import { useEffect, useEffectEvent, useRef, useState } from "react";
import type { ApiClient } from "../api/client.ts";
import { ApiError } from "../api/errors.ts";
import type { components } from "../api/schema";
import { useAppEnvironment } from "../environment.tsx";
import { useAppEvent } from "../realtime/RealtimeProvider.tsx";
import { useApiClient } from "../session/SessionProvider.tsx";
import type { FileUploader } from "./fileUploader.ts";
import type { ImageCompressor } from "./imageCompressor.ts";

type MediaAsset = components["schemas"]["MediaAssetResponse"];

/** Как у backend (`MEDIA_MAX_SIZE_BYTES`): файл в хранилище не больше 5 МБ. */
export const MEDIA_MAX_SIZE_BYTES = 5 * 1024 * 1024;

/** Фото ужимается до этой длинной стороны: больше экрану E-va и ленте не нужно, а файл укладывается в 5 МБ. */
const PHOTO_MAX_SIDE = 1600;
const PHOTO_JPEG_QUALITY = 0.85;

/** Что принимает «Выбрать из галереи». HEIC браузер отдаёт как изображение и сжимает в JPEG вместе с остальными. */
export const GALLERY_ACCEPT = "image/jpeg,image/png,image/gif,image/heic,image/heif";

const COMPRESSIBLE_TYPES = new Set(["image/jpeg", "image/png", "image/heic", "image/heif"]);

export type AttachmentKind = "image" | "gif";

/** Вложение, готовое к загрузке: JPEG после сжатия или GIF как есть. */
export type PreparedAttachment = { kind: AttachmentKind; blob: Blob; contentType: string; fileName: string };

export type AttachmentProblem = "gif_too_large" | "photo_too_large" | "unsupported";

/** Файл нельзя отправить, и повтор этого не изменит: нужен другой файл. */
export class AttachmentError extends Error {
  constructor(readonly problem: AttachmentProblem) {
    super(`Attachment rejected: ${problem}`);
    this.name = "AttachmentError";
  }
}

/**
 * GIF уходит как есть, если он не больше 5 МБ: сжатие через canvas потеряло бы анимацию. JPEG, PNG и HEIC
 * ужимаются в JPEG. Тип файла бывает пустым (HEIC в некоторых браузерах) — тогда решает, прочитал ли его браузер.
 */
export async function prepareAttachment(file: File, compressor: ImageCompressor): Promise<PreparedAttachment> {
  if (file.type === "image/gif") {
    if (file.size > MEDIA_MAX_SIZE_BYTES) throw new AttachmentError("gif_too_large");
    return { kind: "gif", blob: file, contentType: "image/gif", fileName: file.name };
  }
  if (file.type && !COMPRESSIBLE_TYPES.has(file.type)) throw new AttachmentError("unsupported");
  let compressed;
  try {
    compressed = await compressor.compress(file, { maxSide: PHOTO_MAX_SIDE, quality: PHOTO_JPEG_QUALITY });
  } catch {
    throw new AttachmentError("unsupported");
  }
  if (compressed.blob.size > MEDIA_MAX_SIZE_BYTES) throw new AttachmentError("photo_too_large");
  return { kind: "image", blob: compressed.blob, contentType: "image/jpeg", fileName: jpegName(file.name) };
}

function jpegName(fileName: string): string {
  const base = fileName.replace(/\.[^./]*$/, "");
  return `${base || "photo"}.jpg`;
}

/** Докуда дошла загрузка файла: после PUT есть `uploadId`, и повтор начинает сразу с complete. */
type UploadProgress = { uploadId: string | null };

export type AttachmentState =
  | { status: "empty" }
  | { status: "preparing" }
  /** `progress` — доля файла, отправленная в хранилище. */
  | { status: "uploading"; attachment: PreparedAttachment; progress: number }
  /** Файл в хранилище, backend готовит derivative для E-va. `checks` — сколько раз complete переспросили без ответа «готово». */
  | { status: "processing"; attachment: PreparedAttachment; assetId: string; uploadId: string; checks: number }
  | { status: "ready"; attachment: PreparedAttachment; assetId: string }
  /** `attachment` есть — повтор загрузит его же, без нового выбора и сжатия; с `resume.uploadId` — только повторит complete. */
  | { status: "failed"; attachment: PreparedAttachment | null; resume: UploadProgress; error: unknown };

type Processing = Extract<AttachmentState, { status: "processing" }>;

/**
 * Готовность derivative приходит событием `media.processed`. Если его нет (событие потерялось, worker занят),
 * complete переспрашивается каждые 10 секунд; через минуту без ответа «готово» — ошибка с повтором.
 */
const PROCESSING_CHECK_INTERVAL_MS = 10_000;
const PROCESSING_CHECKS = 6;

/**
 * Вложение compose: выбор → подготовка → presign → PUT в хранилище → complete → ожидание derivative.
 * Сообщение с `mediaAssetId` можно создавать только в состоянии `ready`: раньше backend ответит `media_not_processed`.
 */
export function useAttachment() {
  const client = useApiClient();
  const { imageCompressor, uploadFile, timers } = useAppEnvironment();
  const [state, setState] = useState<AttachmentState>({ status: "empty" });
  // Каждый выбор файла, повтор и «Убрать» — новая попытка: результаты прежней, если она ещё идёт, отбрасываются.
  const attempt = useRef(0);
  // `media.processed` может прийти раньше ответа на complete.
  const processed = useRef(new Set<string>());

  function update(run: number, next: AttachmentState) {
    if (run === attempt.current) setState(next);
  }

  function settled(attachment: PreparedAttachment, uploadId: string, asset: MediaAsset, checks: number): AttachmentState {
    if (asset.processingStatus === "processed" || processed.current.has(asset.id)) {
      return { status: "ready", attachment, assetId: asset.id };
    }
    if (checks >= PROCESSING_CHECKS) {
      return { status: "failed", attachment, resume: { uploadId }, error: new Error("Media processing timed out") };
    }
    return { status: "processing", attachment, assetId: asset.id, uploadId, checks };
  }

  async function upload(run: number, attachment: PreparedAttachment, resume: UploadProgress) {
    try {
      const { uploadId, asset } = await uploadAttachment({
        client,
        uploadFile,
        attachment,
        progress: resume,
        onProgress: (fraction) => update(run, { status: "uploading", attachment, progress: fraction }),
      });
      update(run, settled(attachment, uploadId, asset, 0));
    } catch (error) {
      update(run, failed(attachment, resume.uploadId, error));
    }
  }

  /**
   * Переспросить complete. Ответ применяется, только если вложение всё ещё в том же ожидании.
   * В лимит минуты засчитываются только проверки по таймеру: частые `sync_required` его не сокращают.
   */
  async function recheck(current: Processing, { counted }: { counted: boolean }) {
    const { attachment, uploadId } = current;
    const checks = current.checks + (counted ? 1 : 0);
    let next: AttachmentState;
    try {
      next = settled(attachment, uploadId, await completeUpload(client, uploadId, attachment), checks);
    } catch (error) {
      next = failed(attachment, uploadId, error);
    }
    setState((previous) => (previous === current ? next : previous));
  }

  async function pick(file: File) {
    attempt.current += 1;
    const run = attempt.current;
    update(run, { status: "preparing" });
    let attachment: PreparedAttachment;
    try {
      attachment = await prepareAttachment(file, imageCompressor);
    } catch (error) {
      update(run, { status: "failed", attachment: null, resume: { uploadId: null }, error });
      return;
    }
    update(run, { status: "uploading", attachment, progress: 0 });
    await upload(run, attachment, { uploadId: null });
  }

  function retry() {
    if (state.status !== "failed" || !state.attachment) return;
    attempt.current += 1;
    const run = attempt.current;
    // Копия: прогресс этой попытки не должен менять состояние, из которого её начали.
    const resume = { ...state.resume };
    update(run, { status: "uploading", attachment: state.attachment, progress: resume.uploadId ? 1 : 0 });
    void upload(run, state.attachment, resume);
  }

  function clear() {
    attempt.current += 1;
    setState({ status: "empty" });
  }

  const onCheckDue = useEffectEvent((current: Processing) => void recheck(current, { counted: true }));
  useEffect(() => {
    if (state.status !== "processing") return;
    const current = state;
    const id = timers.setTimeout(() => onCheckDue(current), PROCESSING_CHECK_INTERVAL_MS);
    return () => timers.clearTimeout(id);
  }, [state, timers]);

  useAppEvent("media.processed", ({ data }) => {
    processed.current.add(data.assetId);
    if (state.status === "processing" && state.assetId === data.assetId) {
      setState({ status: "ready", attachment: state.attachment, assetId: state.assetId });
    }
  });

  // События могли потеряться (переподключение, сбой обработки): complete идемпотентен и скажет, что с файлом.
  useAppEvent("sync_required", () => {
    if (state.status === "processing") void recheck(state, { counted: false });
  });

  return { state, pick: (file: File) => void pick(file), retry, clear };
}

/**
 * Ошибка загрузки и с какого места её повторять. Отказ backend'а в complete (4xx: файл не прошёл проверку,
 * обработка не удалась) повтором complete не исправить — файл загружается заново. Сеть и 5xx — повтор с того же места.
 */
function failed(attachment: PreparedAttachment, uploadId: string | null, error: unknown): AttachmentState {
  const restart = error instanceof ApiError && error.status < 500;
  return { status: "failed", attachment, resume: { uploadId: restart ? null : uploadId }, error };
}

async function uploadAttachment({
  client,
  uploadFile,
  attachment,
  progress,
  onProgress,
}: {
  client: ApiClient;
  uploadFile: FileUploader;
  attachment: PreparedAttachment;
  /** Пишется по ходу: после PUT в нём `uploadId`, и при ошибке complete повтор не загрузит файл снова. */
  progress: UploadProgress;
  onProgress: (fraction: number) => void;
}): Promise<{ uploadId: string; asset: MediaAsset }> {
  if (!progress.uploadId) {
    const { data, error, response } = await client.POST("/v1/media/uploads/presign", {
      body: { originalFileName: attachment.fileName, contentType: attachment.contentType, sizeBytes: attachment.blob.size },
    });
    if (!data) throw ApiError.from(response, error);
    await uploadFile({
      url: data.uploadUrl,
      method: data.method,
      headers: data.headers,
      body: attachment.blob,
      onProgress,
    });
    progress.uploadId = data.uploadId;
  }
  const { uploadId } = progress;
  return { uploadId, asset: await completeUpload(client, uploadId, attachment) };
}

async function completeUpload(client: ApiClient, uploadId: string, attachment: PreparedAttachment): Promise<MediaAsset> {
  const { data, error, response } = await client.POST("/v1/media/uploads/{uploadId}/complete", {
    params: { path: { uploadId } },
    body: { actualSizeBytes: attachment.blob.size, detectedContentType: attachment.contentType },
  });
  if (!data) throw ApiError.from(response, error);
  return data;
}
